import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import { ocppService } from '../../../services/ocpp/OcppService';
import { StationsLocationsTypes } from './types';
import { MapFilters, hasAnyFilter } from './filterTypes';

/**
 * Shared fetch/filter logic for the map's location search, used by both the
 * Map page and StationsList's filtered path. Owns the hasAnyFilter branch
 * (search endpoint vs default locations endpoint), a request-id race-guard
 * so a slow, stale response can't clobber a newer one, and Sentry reporting
 * on failure.
 */
export function useStationSearch(
  filters: MapFilters,
): {
  stations: StationsLocationsTypes[];
  loading: boolean;
  error: boolean;
} {
  const [stations, setStations] = useState<StationsLocationsTypes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchActive = useRef(0);
  const fetchStations = useCallback(async (f: MapFilters) => {
    const requestId = ++fetchActive.current;
    setLoading(true);
    try {
      const response = hasAnyFilter(f)
        ? await ocppService.getStationsLocationsBySearch({
            address: f.search || undefined,
            connectorTypes:
              f.connectorTypes.length > 0 ? f.connectorTypes : undefined,
            availableNow: f.availableNow || undefined,
            fastCharging: f.fastCharging || undefined,
          })
        : await ocppService.getStationsLocations();
      if (requestId === fetchActive.current) {
        setStations(response);
        setError(false);
      }
    } catch (error) {
      Sentry.captureException(error);
      if (requestId === fetchActive.current) {
        setStations([]);
        setError(true);
      }
    } finally {
      if (requestId === fetchActive.current) setLoading(false);
    }
  }, []);

  const connectorKey = filters.connectorTypes.join(',');
  useEffect(() => {
    fetchStations(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.search,
    filters.availableNow,
    filters.fastCharging,
    connectorKey,
    fetchStations,
  ]);

  return { stations, loading, error };
}
